/**
 * DomainsPage — admin-UI för organization_domains.
 *
 * Funktioner:
 * - Lista alla domäner för admin:ens org
 * - Visa verified/primary/pattern_type-badges
 * - "Verifiera nu" anropar verify-domain edge function (DNS TXT-check)
 * - Lägg till/ta bort domäner (begränsat via RLS till admin-rollen)
 * - "Generera token" för pre-trigger-rader (F-2 seed-rader har ingen token)
 * - TXT-record-instruktioner visas för ej verifierade domäner
 *
 * RLS-kontext (efter F-2a):
 *   - Tabellen organization_domains är INTE publikt läsbar.
 *   - "Authed read of organization_domains"-policyn tillåter admins att se
 *     sina egna rader (inkl. verification_token).
 *   - INSERT/UPDATE/DELETE-policies kräver has_role(auth.uid(), 'admin', org_id).
 *
 * Ref:
 * - audit-reports/domain-mapping-followups/rapport.md F-2 UI (tidigare skippad)
 * - audit-reports/domain-mapping-audit-fixes/rapport.md F-2 UI
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Check,
  X,
  Loader2,
  Copy,
  Plus,
  Trash2,
  ShieldAlert,
  Key,
} from "lucide-react";

interface DomainRow {
  id: string;
  hostname: string;
  organization_id: string | null;
  is_primary: boolean;
  pattern_type: "exact" | "wildcard";
  verified_at: string | null;
  verification_token: string | null;
  verification_requested_at: string | null;
  created_at: string;
}

export default function DomainsPage() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const queryClient = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [newHostname, setNewHostname] = useState("");
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  const orgId = organization?.id ?? null;

  const { data: domains, isLoading } = useQuery<DomainRow[]>({
    queryKey: ["organization_domains", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from("organization_domains" as any)
        .select(
          "id, hostname, organization_id, is_primary, pattern_type, verified_at, verification_token, verification_requested_at, created_at",
        )
        .eq("organization_id", orgId)
        .order("is_primary", { ascending: false })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as DomainRow[];
    },
    enabled: !!orgId,
  });

  const addDomain = useMutation({
    mutationFn: async (hostname: string) => {
      if (!orgId) throw new Error("ingen org");
      const { error } = await supabase.from("organization_domains" as any).insert({
        organization_id: orgId,
        hostname: hostname.toLowerCase().trim(),
        pattern_type: "exact",
        is_primary: false,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["organization_domains"] });
      setAddOpen(false);
      setNewHostname("");
      toast.success(t("domains.added"));
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg);
    },
  });

  const deleteDomain = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("organization_domains" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["organization_domains"] });
      toast.success(t("domains.deleted"));
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg);
    },
  });

  const regenerateToken = useMutation({
    mutationFn: async (id: string) => {
      // Bygg token i samma format som DB-triggern (boply-verify=<hex>)
      const uuid = crypto.randomUUID().replace(/-/g, "");
      const token = `boply-verify=${uuid.slice(0, 32)}`;
      const { error } = await supabase
        .from("organization_domains" as any)
        .update({
          verification_token: token,
          verification_requested_at: new Date().toISOString(),
          verified_at: null,
        } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["organization_domains"] });
      toast.success(t("domains.tokenRegenerated"));
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg);
    },
  });

  const verifyDomain = async (id: string) => {
    setVerifyingId(id);
    try {
      const { data, error } = await supabase.functions.invoke("verify-domain", {
        body: { domainId: id },
      });
      if (error) throw error;
      const resp = data as { verified?: boolean; hint?: string } | null;
      if (resp?.verified) {
        toast.success(t("domains.verifySuccess"));
        void queryClient.invalidateQueries({ queryKey: ["organization_domains"] });
      } else {
        toast.error(resp?.hint ?? t("domains.verifyFailed"));
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t("domains.verifyFailed");
      toast.error(msg);
    } finally {
      setVerifyingId(null);
    }
  };

  const copyToken = async (token: string | null) => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      toast.success(t("domains.copied"));
    } catch {
      // Fallback tyst — browser kan sakna clipboard-API
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground max-w-2xl">
          {t("domains.description")}
        </p>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              {t("domains.add")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("domains.addDialogTitle")}</DialogTitle>
              <DialogDescription>{t("domains.addDialogDescription")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="new-hostname">{t("domains.hostname")}</Label>
              <Input
                id="new-hostname"
                placeholder={t("domains.hostnamePlaceholder")}
                value={newHostname}
                onChange={(e) => setNewHostname(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>
                {t("domains.cancel")}
              </Button>
              <Button
                onClick={() => addDomain.mutate(newHostname)}
                disabled={addDomain.isPending || !newHostname.trim()}
              >
                {addDomain.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t("domains.add")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {(!domains || domains.length === 0) && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            {t("domains.emptyState")}
          </CardContent>
        </Card>
      )}

      {domains?.map((d) => (
        <Card key={d.id}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <CardTitle className="text-base font-mono break-all">{d.hostname}</CardTitle>
                <div className="flex flex-wrap gap-2 mt-2">
                  {d.is_primary && <Badge>{t("domains.primary")}</Badge>}
                  <Badge variant="outline">
                    {d.pattern_type === "wildcard"
                      ? t("domains.wildcard")
                      : t("domains.exact")}
                  </Badge>
                  {d.verified_at ? (
                    <Badge variant="default" className="bg-green-600">
                      <Check className="h-3 w-3 mr-1" />
                      {t("domains.verified")}
                    </Badge>
                  ) : (
                    <Badge variant="destructive">
                      <X className="h-3 w-3 mr-1" />
                      {t("domains.unverified")}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                {!d.verified_at && d.verification_token && (
                  <Button
                    size="sm"
                    onClick={() => void verifyDomain(d.id)}
                    disabled={verifyingId === d.id}
                  >
                    {verifyingId === d.id && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    {t("domains.verify")}
                  </Button>
                )}
                {!d.verification_token && d.pattern_type === "exact" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => regenerateToken.mutate(d.id)}
                    disabled={regenerateToken.isPending}
                  >
                    <Key className="h-4 w-4 mr-2" />
                    {t("domains.generateToken")}
                  </Button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" aria-label={t("domains.delete")}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("domains.deleteConfirmTitle")}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("domains.deleteConfirm", { hostname: d.hostname })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("domains.cancel")}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => deleteDomain.mutate(d.id)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {t("domains.delete")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardHeader>
          {!d.verified_at && d.verification_token && (
            <CardContent className="pt-0">
              <div className="rounded-md bg-muted p-3 space-y-2 text-sm">
                <p className="font-medium flex items-start gap-2">
                  <ShieldAlert className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
                  {t("domains.verificationInstructions")}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-[auto_auto_1fr] gap-3 font-mono text-xs">
                  <div>
                    <p className="text-muted-foreground mb-1 font-sans">
                      {t("domains.recordType")}
                    </p>
                    <p>TXT</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1 font-sans">
                      {t("domains.recordHost")}
                    </p>
                    <p>{d.hostname}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1 font-sans">
                      {t("domains.recordValue")}
                    </p>
                    <div className="flex items-start gap-2">
                      <code className="flex-1 break-all">{d.verification_token}</code>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="shrink-0 h-6 w-6"
                        onClick={() => void copyToken(d.verification_token)}
                        aria-label="Copy"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}
